import React, { useState } from "react";
import { Play } from "lucide-react";

/**
 * Click-to-play facade for the hero promo video (Streamable).
 *
 * The real embed used to mount on first paint: an iframe that pulls the whole
 * Streamable player (JS, CSS, fonts, analytics, video manifest) into the
 * critical path of every home-page load, whether or not the visitor ever
 * pressed play. On mobile that was the single largest third-party cost on the
 * page. The facade paints a static poster + play button (one small WebP) and
 * only swaps in the iframe — with autoplay — once the visitor clicks.
 *
 * The source video is portrait (3:4); the player letterboxes it inside the
 * 16:9 frame on a dark background, and the poster reproduces that look.
 */
export const VideoFacade = ({
  embedUrl,
  poster,
  posterSrcSet,
  posterSizes,
  posterWidth,
  posterHeight,
  title,
  className = "",
  testId = "hero-streamable",
}) => {
  const [playing, setPlaying] = useState(false);

  return (
    <div
      className={`relative w-full aspect-video rounded-xl overflow-hidden border border-border shadow-sm bg-[#0b0f14] ${className}`}
      data-testid={testId}
    >
      {playing ? (
        <iframe
          src={`${embedUrl}${embedUrl.includes("?") ? "&" : "?"}autoplay=1`}
          title={title}
          className="absolute inset-0 w-full h-full border-none"
          allow="fullscreen;autoplay"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${title}`}
          className="group absolute inset-0 w-full h-full flex items-center justify-center qv-focus"
          data-testid={`${testId}-play`}
        >
          <img
            src={poster}
            srcSet={posterSrcSet}
            sizes={posterSizes}
            alt=""
            width={posterWidth}
            height={posterHeight}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-contain"
            draggable={false}
          />
          <span
            className="relative inline-flex items-center justify-center h-16 w-16 md:h-20 md:w-20 rounded-full bg-white/95 text-brand-blue shadow-lg transition-transform duration-200 group-hover:scale-105"
            aria-hidden="true"
          >
            <Play className="h-7 w-7 md:h-8 md:w-8 ml-1" strokeWidth={0} fill="currentColor" />
          </span>
        </button>
      )}
    </div>
  );
};

export default VideoFacade;
